// ═══════════════════════════════════════════════════════════════════════════
// grainMarketingStagePolicy.js — a SOURCED crop-specific marketing-STAGE
// target-position policy (Kevin, 2026-08-19 review comment: "the current
// target-position model is just the old 15/30% tranche re-labeled... build a
// defensible target range by crop/crop-year/time of marketing year. Use a
// sourced policy, not an invented one.").
//
// SOURCE — University of Minnesota Center for Farm Financial Management
// (CFFM) / Ed Usset teaching grain-marketing plans, cited directly by Kevin:
//   corn:      https://www.cffm.umn.edu/grain-marketing-plans/2026-corn-pre-harvest/
//   soybeans:  https://www.cffm.umn.edu/grain-marketing-plans/2026-soybean-pre-harvest/
//   wheat:     https://www.cffm.umn.edu/grain-marketing-plans/2026-spring-wheat-pre-harvest/
//   overview:  https://www.cffm.umn.edu/grain-marketing-plans/
//   post-harv: https://www.cffm.umn.edu/grain-marketing-plans/2025-corn-post-harvest/
//
// WHAT IS ACTUALLY REUSED FROM CFFM, AND WHAT IS NOT (rule zero — say what is
// sourced and what is an inference, never blend the two):
//   SOURCED, verbatim from the three 2026 pre-harvest plans named above:
//     - the plan STARTS Jan 1 of the crop year;
//     - the OBJECTIVE is 75% of the anticipated/APH crop priced by mid-June,
//       for all three crops;
//     - sales BEFORE that mid-June push are CAPPED — corn 30% (staged 15%
//       first sale then 10% increments), soybeans ~37% (10,000 of an assumed
//       27,000-bu crop), wheat ~45% (15,000 of an assumed 33,000-bu crop).
//   NOT sourced from CFFM, disclosed separately in STAGE_POLICY_SOURCE.caveat
//   and never presented as a CFFM number:
//     - the exact day used to flip from EARLY_PREHARVEST to MIDJUNE_OBJECTIVE
//       (taken literally as June 15, CFFM's own "mid-June" language);
//     - each crop's harvest-start date, which CFFM's pre-harvest plans do not
//       state (they end at the mid-June objective) — this file uses a
//       general US harvest-season convention per crop, named per-crop below;
//     - the 100% "fully marketed" ceiling once past harvest — CFFM's
//       post-harvest plans work from storage/carry/basis targets and an exit
//       date, not a single number; 100% by the stage's own definition here is
//       this file's own disclosed convention (full marketing is the natural
//       ceiling once the crop is out of the field), not a CFFM figure.
//   Kevin's own instruction: "Do not blindly transplant CFFM's exact PRICE
//   thresholds to WNY... the useful sourced policy is the discipline/timing/
//   percentage framework." No CFFM price level appears anywhere in this file
//   — only the percentage-by-stage framework.
//
// WHEAT CAVEAT: CFFM's wheat plan is for SPRING wheat. WNY (this app's
// service area, CLAUDE.md FALL_CROPS) grows WINTER wheat, planted the fall
// before and harvested mid-summer — a materially different calendar from
// spring wheat (planted spring, harvested late summer). This file reuses
// CFFM's wheat TIMING/PERCENTAGE framework as the least-precise of the three
// crops and says so in STAGE_POLICY_SOURCE.caveat; it is a directional
// benchmark, not a winter-wheat-specific plan.
//
// market opportunity (grainAction.js's own STRONG/ATTRACTIVE/WATCH/etc.
// ladder) is NOT used here to build the stage range — it decides WHERE
// within the range to place a target (grainTargetPosition.js does that).
// This file answers ONLY "what stage is this crop-year in, and what does
// CFFM's discipline framework say the floor/ceiling should be by now" — a
// pure function of crop + crop-year + calendar date, never of price.
//
// STRUCTURALLY ISOLATED the same way grainMarketingClock.js is: nothing here
// imports grainAction.js/grainCash.js, and this file computes no price, no
// percentile, no futures/basis reading.
//
// PURE — no fetch, no React, no Supabase, no Date.now()/new Date() (the
// caller supplies `asOf`; this file never reads the clock itself).
// ═══════════════════════════════════════════════════════════════════════════

export const STAGE_POLICY_STATE = { METHODOLOGY_REQUIRED: "METHODOLOGY_REQUIRED", RESOLVED: "RESOLVED" };

export const STAGE_POLICY_SOURCE = {
  name: "CFFM/Usset teaching grain-marketing-plan framework (University of Minnesota Center for Farm Financial Management)",
  citedBy: "Kevin, 2026-08-19 PR review comment",
  urls: {
    overview: "https://www.cffm.umn.edu/grain-marketing-plans/",
    corn: "https://www.cffm.umn.edu/grain-marketing-plans/2026-corn-pre-harvest/",
    soybeans: "https://www.cffm.umn.edu/grain-marketing-plans/2026-soybean-pre-harvest/",
    wheat: "https://www.cffm.umn.edu/grain-marketing-plans/2026-spring-wheat-pre-harvest/",
    cornPostHarvest: "https://www.cffm.umn.edu/grain-marketing-plans/2025-corn-post-harvest/",
  },
  caveat: [
    "CFFM presents these as teaching plans, not a universal or 'perfect' plan; their price triggers are Minnesota-area illustrative levels. This repo reuses ONLY the timing/discipline/percentage-cap framework — plan-start date, pre-mid-June cap, mid-June 75% objective — never a CFFM price level.",
    "Wheat uses CFFM's SPRING wheat plan as the sourced timing/percentage framework. WNY (this app's service area) grows WINTER wheat, whose planting/harvest calendar differs from spring wheat's — wheat stage dates here are the least precise of the three crops and are a directional benchmark, not a winter-wheat-specific plan.",
    "Per-crop harvest-start dates and the 100% post-harvest ceiling are this file's OWN disclosed convention, not stated in the CFFM pre-harvest plans (which end at the mid-June objective) — see the header comment for exactly what is and is not sourced.",
  ],
};

// Sourced directly from the three 2026 CFFM pre-harvest plans (see header):
//   earlyCapPct         — cap on sales BEFORE the mid-June push.
//   midJuneObjectivePct — CFFM's own "75% by mid-June" objective, identical
//                         across all three crops.
//   harvestStartMonthDay — NOT from CFFM; this file's own general
//                         harvest-season convention, disclosed above.
const CROP_PLAN = {
  corn:     { earlyCapPct: 30, midJuneObjectivePct: 75, harvestStartMonthDay: [9, 15] },
  soybeans: { earlyCapPct: 37, midJuneObjectivePct: 75, harvestStartMonthDay: [9, 1] },
  wheat:    { earlyCapPct: 45, midJuneObjectivePct: 75, harvestStartMonthDay: [7, 1] },
};

// normalizeCropKey — collapses WHEAT / WINTER WHEAT to one key, the same
// convention grainRules.js/grainPosition.js's own cropMatches() already uses
// for crop-name matching (duplicated here rather than imported, for the same
// structural reason grainCommitment.js's header documents: importing
// cropMatches from grainPosition.js would create a two-way cycle once a
// caller imports this file's exports back into a chain that reaches
// grainPosition.js — this file stays leaf-level and dependency-free).
function normalizeCropKey(crop) {
  const c = String(crop || "").trim().toLowerCase();
  if (c === "corn") return "corn";
  if (c === "soybeans" || c === "soybean" || c === "soy" || c === "soybeans/soymeal") return "soybeans";
  if (c.startsWith("wheat") || c.startsWith("winter wheat") || c.startsWith("spring wheat")) return "wheat";
  return null;
}

// parseAsOf — 'YYYY-MM-DD' only. Never Date.now()/new Date(); this file is
// PURE and the caller (App.jsx, which does have a real clock) supplies the
// date to evaluate against.
function parseAsOf(asOf) {
  if (typeof asOf !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(asOf.trim());
  if (!m) return null;
  const year = Number(m[1]), month = Number(m[2]), day = Number(m[3]);
  if (!Number.isFinite(year) || month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

// monthDayOrdinal — sortable within-one-year comparator, e.g. Jun 15 -> 615.
function monthDayOrdinal(month, day) { return month * 100 + day; }

// resolveMarketingStagePolicy({ crop, cropYear, asOf }) — places a crop-year
// within CFFM's own discipline calendar and returns the sourced floor/
// ceiling band for that stage. Returns known:false (methodologyRequired)
// whenever the crop isn't one of the three sourced plans, or asOf/cropYear
// aren't real values to place the farm within its marketing calendar —
// NEVER a guessed range for a crop or date this file has no source for.
//
//   crop     — crop name, or null.
//   cropYear — the crop year in question, or null.
//   asOf     — 'YYYY-MM-DD' the caller is evaluating as-of, or null.
export function resolveMarketingStagePolicy({ crop = null, cropYear = null, asOf = null } = {}) {
  const cropKey = normalizeCropKey(crop);
  const inputs = {
    crop: crop || null,
    cropYear: cropYear != null && Number.isFinite(Number(cropYear)) ? Number(cropYear) : null,
    asOf: asOf || null,
  };
  const plan = cropKey ? CROP_PLAN[cropKey] : null;
  const parsed = parseAsOf(asOf);

  if (!plan || !parsed || inputs.cropYear == null) {
    return {
      known: false,
      state: STAGE_POLICY_STATE.METHODOLOGY_REQUIRED,
      methodologyRequired: true,
      stage: null,
      inputs,
      source: STAGE_POLICY_SOURCE,
      range: null,
      reasons: [
        !plan ? `no sourced CFFM stage plan for crop "${crop}" (sourced: corn, soybeans, wheat)` : null,
        !parsed ? "asOf must be a real 'YYYY-MM-DD' date to place this crop-year within CFFM's marketing calendar" : null,
        inputs.cropYear == null ? "cropYear is required — CFFM's plan is anchored to Jan 1 of the crop's OWN marketing year" : null,
      ].filter(Boolean),
    };
  }

  if (parsed.year < inputs.cropYear) {
    return {
      known: false,
      state: STAGE_POLICY_STATE.METHODOLOGY_REQUIRED,
      methodologyRequired: true,
      stage: "PRE_PLAN",
      inputs,
      source: STAGE_POLICY_SOURCE,
      range: null,
      reasons: [`as-of date is before ${inputs.cropYear}'s CFFM plan starts (Jan 1, ${inputs.cropYear}) — no stage benchmark applies yet`],
    };
  }

  const md = monthDayOrdinal(parsed.month, parsed.day);
  const [hMonth, hDay] = plan.harvestStartMonthDay;
  const harvestMd = monthDayOrdinal(hMonth, hDay);
  const midJuneMd = 615;

  let stage, floorPct, ceilingPct, reason;
  if (parsed.year > inputs.cropYear || md >= harvestMd) {
    stage = "HARVEST_AND_POST";
    floorPct = plan.midJuneObjectivePct;
    ceilingPct = 100;
    reason = "at/after this crop's typical harvest start — CFFM's plans progress toward full marketing through harvest and post-harvest storage decisions; the 100% ceiling here is this file's own disclosed convention, not a specific CFFM number";
  } else if (md >= midJuneMd) {
    stage = "MIDJUNE_OBJECTIVE";
    floorPct = plan.earlyCapPct;
    ceilingPct = plan.midJuneObjectivePct;
    reason = `CFFM objective: ${plan.midJuneObjectivePct}% of the anticipated/APH crop priced by mid-June`;
  } else {
    stage = "EARLY_PREHARVEST";
    floorPct = 0;
    ceilingPct = plan.earlyCapPct;
    reason = `CFFM caps sales before the mid-June push at ${plan.earlyCapPct}% for this crop`;
  }

  return {
    known: true,
    state: STAGE_POLICY_STATE.RESOLVED,
    methodologyRequired: false,
    stage,
    inputs,
    source: STAGE_POLICY_SOURCE,
    range: { floorPct, ceilingPct },
    reasons: [reason],
  };
}
